import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import type { FullUser } from '@/graphql/__generated/resolvers-types';
import { appLogger } from '@/utils/app-logger/appLogger';

import { buildAdobeUserTrackingFields } from './buildAdobeUserTrackingFields';
import {
    getSubscriberUtagSyncKey,
    isProfilePickerPageType,
    isProfilePickerRoute,
} from './isUserDataReadyForPageView';

/**
 * Fire trackState once subscriber fields are in Adobe on profile-picker routes.
 * Full document loads (e.g. login → Who's Watching) do not get routeChangeComplete,
 * so navigation-only readDataLayerPageUpdate is insufficient for CBSSITE-14145.
 */
export function fireProfilePickerPageLoadBeacon(
    userFullProfile: FullUser | undefined | null,
    pageType: string | null | undefined,
    lastFiredSyncKeyRef: { current: string | null },
    pathname?: string | null,
): boolean {
    // Prefer live browser URL — React-captured pathname can lag UPDATE_DATA_LAYER on routeChangeComplete.
    const currentPathname =
        typeof window !== 'undefined' ? window.location.pathname : (pathname ?? null);

    if (!isProfilePickerRoute(pageType, currentPathname)) {
        return false;
    }

    const syncKey = getSubscriberUtagSyncKey(userFullProfile, pageType);
    if (!syncKey || lastFiredSyncKeyRef.current === syncKey) {
        return false;
    }

    if (typeof window === 'undefined') {
        return false;
    }

    const flatUserFields = buildAdobeUserTrackingFields(userFullProfile as FullUser);
    window.utag_data = window.utag_data || {};
    Object.assign(window.utag_data, flatUserFields);

    if (typeof window.readDataLayerPageUpdate !== 'function') {
        return false;
    }

    try {
        window.readDataLayerPageUpdate();
        BootstrapSignals.mark(BootstrapSignalKeys.PAGE_VIEW_BEACON_FIRED);
    } catch (error) {
        appLogger.warn('fireProfilePickerPageLoadBeacon: readDataLayerPageUpdate not ready', {
            component: 'fireProfilePickerPageLoadBeacon',
            errorCode: error instanceof Error ? error.name : undefined,
            errorMessage: error instanceof Error ? error.message : String(error),
        });
        return false;
    }

    lastFiredSyncKeyRef.current = syncKey;
    return true;
}

/**
 * Post-bootstrap utag sync can run when pageType changes during MFE client navigation.
 * routeChangeComplete already invoked readDataLayerPageUpdate — skip a second beacon then.
 * Still re-fire when subscriber fields enrich on the same profile-picker page.
 */
export function shouldFirePickerBeaconOnSubscriberSyncKeyChange(
    previousSyncKey: string | null,
    nextSyncKey: string,
): boolean {
    if (!previousSyncKey) {
        return false;
    }

    const previousPageType = previousSyncKey.split('|')[0] ?? '';
    const nextPageType = nextSyncKey.split('|')[0] ?? '';

    if (previousPageType !== nextPageType) {
        return false;
    }

    return previousSyncKey !== nextSyncKey;
}

/**
 * PageLayout sets pageType in a child effect after AppShell bootstrap may already
 * have synced subscriber fields with an empty pageType segment. Also covers
 * home → picker client navigation where routeChangeComplete defers readDataLayerPageUpdate.
 */
export function shouldFirePickerBeaconOnPageTypeResolve(
    previousSyncKey: string | null,
    nextSyncKey: string,
): boolean {
    const nextPageType = nextSyncKey.split('|')[0] ?? '';
    if (!isProfilePickerPageType(nextPageType)) {
        return false;
    }

    if (!previousSyncKey) {
        return true;
    }

    const previousPageType = previousSyncKey.split('|')[0] ?? '';
    if (previousPageType === nextPageType) {
        return false;
    }

    return previousPageType === '' || !isProfilePickerPageType(previousPageType);
}
